import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './auth';
import { type ViewContext, useViewContexts } from './contexts';

/** Where the last chosen context is remembered (localStorage on the web, AsyncStorage on phones). */
export interface ChoiceStorage {
  get: () => string | null | Promise<string | null>;
  set: (key: string) => void | Promise<void>;
}

interface ViewContextValue {
  contexts: ViewContext[];
  current: ViewContext | undefined;
  select: (key: string) => void;
  loading: boolean;
}

const Ctx = createContext<ViewContextValue | null>(null);

/** Which child / role the person is looking at right now; remembered between visits. */
export function ViewContextProvider({
  storage,
  children,
}: {
  storage?: ChoiceStorage;
  children: ReactNode;
}) {
  const { me } = useAuth();
  const query = useViewContexts(me);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void Promise.resolve(storage?.get() ?? null)
      .catch(() => null)
      .then((key) => {
        if (active && key) setSelected((current) => current ?? key);
      });
    return () => {
      active = false;
    };
  }, [storage]);

  const value = useMemo<ViewContextValue>(() => {
    const contexts = query.data ?? [];
    const current = contexts.find((c) => c.key === selected) ?? contexts[0];
    return {
      contexts,
      current,
      loading: query.isPending,
      select: (key) => {
        void Promise.resolve(storage?.set(key)).catch(() => undefined);
        setSelected(key);
      },
    };
  }, [query.data, query.isPending, selected, storage]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useViewContext(): ViewContextValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useViewContext must be used inside <ViewContextProvider>');
  return value;
}

/** The current context, for screens that render only once it is known. */
export function useCurrentContext(): ViewContext {
  const { current } = useViewContext();
  if (!current) throw new Error('no current context');
  return current;
}
